/* eslint-disable react/require-default-props */
import React, { useState } from "react";
import {
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import { useIntl } from "../../../../../_metronic/i18n/customUseIntl";
import { dateDisplayOptionsYMDHM } from "../../../utils/datetime-utils";
import { notificationTableStyles } from "../../../../ThemeProvider";
import type {
  AppNotification,
  NotificationCategory,
} from "../../../models/Notification/notificationTypes";

type NotificationTableProps = {
  category: NotificationCategory;
  data: AppNotification[];
  isLoading: boolean;
  error: Error | null;
  selectedId: string | null;
  onRowClick: (notification: AppNotification) => void;
};

const NotificationTable = ({
  category,
  data,
  isLoading,
  error,
  selectedId,
  onRowClick,
}: NotificationTableProps) => {
  const intl = useIntl();
  const classes = notificationTableStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" padding={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2" className={classes.errorText}>
        {error.message}
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table aria-label="notifications table">
        <TableHead>
          <TableRow>
            <TableCell align="left">
              {intl.formatMessage({ id: "NOTIFICATIONS.TITLE" })}
            </TableCell>
            <TableCell align="right">
              {intl.formatMessage({ id: "NOTIFICATIONS.PUBLISH_TIME" })}
            </TableCell>
            <TableCell align="right">
              {intl.formatMessage({ id: "NOTIFICATIONS.EXPIRATION_TIME" })}
            </TableCell>
            {category === "Notification" && (
              <TableCell align="right">
                {intl.formatMessage({ id: "NOTIFICATIONS.WELCOME_MESSAGE" })}
              </TableCell>
            )}
            <TableCell align="right">
              {intl.formatMessage({ id: "NOTIFICATIONS.ACTIVE" })}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row) => (
              <TableRow
                key={row.id}
                hover
                className={classes.clickableRow}
                onClick={() => onRowClick(row)}
                selected={selectedId === row.id}
              >
                <TableCell align="left">{row.title}</TableCell>
                <TableCell align="right">
                  {row.publishAt
                    ? intl.formatDate(
                        row.publishAt,
                        dateDisplayOptionsYMDHM as Intl.DateTimeFormatOptions,
                      )
                    : ""}
                </TableCell>
                <TableCell align="right">
                  {row.expiresAt
                    ? intl.formatDate(
                        row.expiresAt,
                        dateDisplayOptionsYMDHM as Intl.DateTimeFormatOptions,
                      )
                    : ""}
                </TableCell>
                {category === "Notification" && (
                  <TableCell align="right">
                    {row.isWelcome ? <CheckIcon /> : <CloseIcon />}
                  </TableCell>
                )}
                <TableCell align="right">
                  {row.active ? <CheckIcon /> : <CloseIcon />}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          labelRowsPerPage={intl.formatMessage({ id: "GENERAL.ROWS_PER_PAGE" })}
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_: unknown, newPage: number) => setPage(newPage)}
          onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
        />
      </Table>
    </TableContainer>
  );
};

export default NotificationTable;
